import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RefreshCw, Info, AlertTriangle, CheckCircle2 } from "lucide-react";
import AdminSideBar from "./AdminSideBar";
import { authFetch } from "../utils/auth";
import UserProfileMenu from "./UserProfileMenu";

// Boolean toggles this page owns. Mirrors _ADMIN_FLAGS in api/v1/auth_users.py —
// adding a switch is a row here and a row there, not a new state pair, a new
// payload branch and a new block of markup.
const FLAGS = [
  {
    key: "semantic_cache_enabled",
    label: "Semantic Response Cache",
    description:
      "When enabled, similar queries are served from a Redis cache to reduce response time and API cost.",
  },
  {
    key: "learning_enabled",
    label: "Learning Module",
    description:
      "When enabled, Arta records how each conversation was diagnosed — every step tried, in order — for an engineer to review. Nothing is used to answer until it is reviewed and published.",
  },
  {
    key: "learning_retrieval_enabled",
    label: "Learning Retrieval",
    description:
      "When enabled, published trails inform new answers: the engine can see which branches worked for a similar symptom and which dead-ended. Turn this on only after reviewing the first trails.",
    requires: "learning_enabled",
  },
];

const Toggle = ({ on, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${
      on ? "bg-cyan-500" : "bg-gray-300"
    } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        on ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const AdminSettingsPage = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [notifyEmail, setNotifyEmail] = useState("email@artasupport.ai");
  const [savedNotifyEmail, setSavedNotifyEmail] = useState("email@artasupport.ai");
  const [senderEmail, setSenderEmail] = useState("email@artasupport.ai");
  const [savedSenderEmail, setSavedSenderEmail] = useState("email@artasupport.ai");
  const [tseModel, setTseModel] = useState("");
  const [savedTseModel, setSavedTseModel] = useState("");
  const [tseModelChoices, setTseModelChoices] = useState([]);
  const [flags, setFlags] = useState({});
  const [savedFlags, setSavedFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch("/api/admin/settings");
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Failed to load settings.");
      }
      const data = await res.json();
      setSavedNotifyEmail(data.admin_notify_email || "email@artasupport.ai");
      setNotifyEmail(data.admin_notify_email || "email@artasupport.ai");
      setSavedSenderEmail(data.ses_sender_email || "email@artasupport.ai");
      setSenderEmail(data.ses_sender_email || "email@artasupport.ai");
      setSavedTseModel(data.tse_anthropic_model || "");
      setTseModel(data.tse_anthropic_model || "");
      setTseModelChoices(data.tse_model_choices || []);
      const loaded = Object.fromEntries(FLAGS.map((f) => [f.key, !!data[f.key]]));
      setSavedFlags(loaded);
      setFlags(loaded);
    } catch (err) {
      setError(err.message || "Failed to load settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const body_payload = {
        admin_notify_email: notifyEmail.trim(),
        ses_sender_email: senderEmail.trim(),
      };
      if (tseModel !== savedTseModel) {
        body_payload.tse_anthropic_model = tseModel;
      }
      FLAGS.forEach((f) => {
        if (flags[f.key] !== savedFlags[f.key]) body_payload[f.key] = flags[f.key];
      });
      const res = await authFetch("/api/admin/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body_payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Failed to save settings.");
      }
      const modelChanged = tseModel !== savedTseModel;
      const changedFlags = FLAGS.filter((f) => flags[f.key] !== savedFlags[f.key]);
      setSavedNotifyEmail(notifyEmail.trim());
      setSavedSenderEmail(senderEmail.trim());
      setSavedTseModel(tseModel);
      setSavedFlags({ ...flags });
      const msgs = [];
      if (modelChanged) msgs.push("Model updated — new sessions will use " + tseModel);
      changedFlags.forEach((f) =>
        msgs.push(f.label + " " + (flags[f.key] ? "enabled" : "disabled") + " — takes effect from new sessions")
      );
      setSuccess(msgs.length ? "Settings saved. " + msgs.join(". ") + "." : "Settings saved.");
    } catch (err) {
      setError(err.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const flagsDirty = FLAGS.some((f) => flags[f.key] !== savedFlags[f.key]);
  const isDirty =
    notifyEmail.trim() !== savedNotifyEmail ||
    senderEmail.trim() !== savedSenderEmail ||
    tseModel !== savedTseModel ||
    flagsDirty;

  // A dependent switch cannot be on while its parent is off, and flipping the
  // parent off must carry its dependants with it — otherwise the UI shows
  // "Learning Retrieval: on" while the master switch silently disables it.
  const setFlag = (key, value) => {
    setFlags((prev) => {
      const next = { ...prev, [key]: value };
      if (!value) FLAGS.forEach((f) => { if (f.requires === key) next[f.key] = false; });
      return next;
    });
    setSuccess("");
  };

  return (
    <div className="flex h-screen w-full bg-[#FAFAF6] overflow-hidden">
      <AdminSideBar onLogout={onLogout} />

      {/* Main content */}
      <div className="flex-1 relative flex flex-col px-6 sm:px-10 lg:px-16 xl:px-24 py-8 overflow-auto">
        <div className="absolute top-4 right-6 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>
        <div className="w-full">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#253439]">Settings</h1>
              <p className="text-gray-500 text-sm mt-1">
                Configure system-wide admin preferences.
              </p>
            </div>
            <button
              onClick={fetchSettings}
              disabled={loading}
              className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="mb-4 bg-emerald-50 border border-emerald-300 text-emerald-800 px-4 py-3 rounded-lg text-sm font-medium flex items-start gap-2 shadow-sm">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {loading ? (
            <div className="text-center py-16 text-gray-400">Loading…</div>
          ) : (
            <form onSubmit={handleSave} className="space-y-6">
              {/* ── Email & Notifications ── */}
              <div className="bg-white border border-[#EBECE6] rounded-xl shadow-sm p-6">
                <h2 className="text-base font-bold text-[#253439] mb-1">Email & Notifications</h2>
                <p className="text-sm text-gray-400 mb-5">Configure addresses used for signup approvals and outgoing system emails.</p>

                <div className="mb-5">
                  <label className="block text-sm font-semibold text-[#253439] mb-1">
                    Signup notification email
                  </label>
                  <p className="text-sm text-gray-400 mb-3">
                    When a new user signs up, an approval request is sent to this address.
                    Leave empty to block all new signups until configured.
                  </p>
                  <input
                    type="email"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    placeholder="email@artasupport.ai"
                    value={notifyEmail}
                    onChange={(e) => { setNotifyEmail(e.target.value); setSuccess(""); }}
                  />
                  {!savedNotifyEmail && (
                    <div className="mt-3 bg-amber-50 border border-amber-300 text-amber-800 px-3 py-2.5 rounded-lg text-sm flex items-start gap-2">
                      <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                      <span>Not configured — signups are currently blocked until this is set.</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#253439] mb-1">
                    Email sender address (SES)
                  </label>
                  <p className="text-sm text-gray-400 mb-3">
                    The "From" address on all outgoing emails. Must be a verified address in AWS SES.
                    Defaults to the notification email above if left empty.
                  </p>
                  <input
                    type="email"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    placeholder="email@artasupport.ai"
                    value={senderEmail}
                    onChange={(e) => { setSenderEmail(e.target.value); setSuccess(""); }}
                  />
                  {!savedSenderEmail && senderEmail && (
                    <div className="mt-3 bg-gray-50 border border-gray-200 text-gray-500 px-3 py-2.5 rounded-lg text-sm flex items-start gap-2">
                      <Info size={16} className="mt-0.5 flex-shrink-0" />
                      <span>Using default from server configuration. Save to override.</span>
                    </div>
                  )}
                </div>
              </div>

              {/* ── AI Agent ── */}
              <div className="bg-white border border-[#EBECE6] rounded-xl shadow-sm p-6">
                <h2 className="text-base font-bold text-[#253439] mb-1">AI Agent</h2>
                <p className="text-sm text-gray-400 mb-5">Configure the AI model and performance optimizations used by the agent.</p>

                <div className="mb-5">
                  <label className="block text-sm font-semibold text-[#253439] mb-1">
                    Model
                  </label>
                  <p className="text-sm text-gray-400 mb-3">
                    Select the AI model that powers agent sessions.
                  </p>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                    value={tseModel}
                    onChange={(e) => { setTseModel(e.target.value); setSuccess(""); }}
                  >
                    {tseModelChoices.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  {tseModel !== savedTseModel && (
                    <div className="mt-3 bg-amber-50 border border-amber-300 text-amber-800 px-3 py-3 rounded-lg text-sm font-medium flex items-start gap-2">
                      <Info size={16} className="mt-0.5 flex-shrink-0" />
                      <div className="space-y-1.5">
                        <p><strong>This change will only affects new sessions after saving</strong></p>
                        <p className="text-amber-700">To apply this change across all sessions immediately, the server must be restarted. This will disconnect all active users and terminate their ongoing sessions.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* One divided group, so a run of switches reads as a list
                    instead of three paragraphs run together. A dependent switch
                    is indented under its parent — the relationship has to be
                    visible, not only enforced on click. */}
                <div className="border-t border-[#EBECE6] divide-y divide-[#EBECE6]">
                {FLAGS.map((f) => {
                  const blocked = f.requires && !flags[f.requires];
                  return (
                    <div
                      key={f.key}
                      className={`py-4 ${f.requires ? "pl-4 border-l-2 border-[#EBECE6] ml-1" : ""}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="block text-sm font-semibold text-[#253439] mb-1">
                            {f.label}
                          </label>
                          <p className="text-sm text-gray-400 max-w-3xl leading-relaxed">
                            {f.description}
                          </p>
                          {blocked && (
                            <p className="text-xs text-gray-400 mt-1 italic">
                              Requires {FLAGS.find((x) => x.key === f.requires)?.label}.
                            </p>
                          )}
                        </div>
                        <Toggle
                          on={!!flags[f.key]}
                          disabled={blocked}
                          onClick={() => setFlag(f.key, !flags[f.key])}
                        />
                      </div>
                      {flags[f.key] !== savedFlags[f.key] && (
                        <div className="mt-3 bg-amber-50 border border-amber-300 text-amber-800 px-3 py-3 rounded-lg text-sm font-medium flex items-start gap-2">
                          <Info size={16} className="mt-0.5 flex-shrink-0" />
                          <div className="space-y-1.5">
                            <p><strong>This change will only affect new sessions after saving</strong></p>
                            <p className="text-amber-700">To apply this change across all sessions immediately, the server must be restarted. This will disconnect all active users and terminate their ongoing sessions.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving || !isDirty}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold px-6 py-2 rounded-lg text-sm transition disabled:opacity-40"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
